import type { TripSegment } from '../types'

interface Props {
	segments: TripSegment[]
	selectedTripId: string | null
	onSelectTrip: (tripId: string | null) => void
	savedTripIds: Set<string>
	onToggleSave: (tripId: string) => void
}

export function ListView({
	segments,
	selectedTripId,
	onSelectTrip,
	savedTripIds,
	onToggleSave,
}: Props) {
	const formatDateTime = (isoDate: string | undefined) => {
		if (!isoDate) return '—'
		try {
			const date = new Date(isoDate)
			return date.toLocaleString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
			})
		} catch {
			return '—'
		}
	}

	const formatTrailer = (trailer: string | undefined) => {
		if (!trailer) return '—'
		// Strip trailing .0 from numeric-looking values (e.g., "1423424.0" -> "1423424")
		return trailer.replace(/\.0$/, '')
	}

	const getStatusBadge = (segment: TripSegment) => {
		const status = segment.shipment_status || segment.order_status
		let label = status || 'Unknown'
		let bgColor = '#e5e7eb'
		let textColor = '#374151'

		switch (segment.order_status) {
			case 'D':
				label = 'Delivered'
				bgColor = '#dcfce7'
				textColor = '#166534'
				break
			case 'P':
				label = 'Picked Up'
				bgColor = '#dbeafe'
				textColor = '#1e40af'
				break
			case 'A':
				label = 'At Shipper'
				bgColor = '#fef3c7'
				textColor = '#92400e'
				break
			case 'V':
				label = 'Void'
				bgColor = '#fecaca'
				textColor = '#991b1b'
				break
			default:
				if (segment.shipment_status) {
					label = segment.shipment_status
					if (segment.shipment_status === 'Delivered') {
						bgColor = '#dcfce7'
						textColor = '#166534'
					} else if (segment.shipment_status === 'Picked Up') {
						bgColor = '#dbeafe'
						textColor = '#1e40af'
					}
				}
		}

		return (
			<span
				style={{
					display: 'inline-block',
					padding: '2px 8px',
					borderRadius: '4px',
					fontSize: '11px',
					fontWeight: 600,
					backgroundColor: bgColor,
					color: textColor,
					whiteSpace: 'nowrap',
				}}
			>
				{label}
			</span>
		)
	}

	return (
		<div style={styles.container}>
			<table style={styles.table}>
				<thead>
					<tr style={styles.headerRow}>
						<th style={{ ...styles.th, width: '50px' }}>Save</th>
						<th style={{ ...styles.th, width: '100px' }}>Pro Number</th>
						<th style={{ ...styles.th, width: '100px' }}>Order Status</th>
						<th style={{ ...styles.th, width: '150px' }}>Customer</th>
						<th style={{ ...styles.th, width: '120px' }}>BOL Number</th>
						<th style={styles.th}>Shipper</th>
						<th style={styles.th}>Consignee</th>
						<th style={{ ...styles.th, width: '100px' }}>Trailer</th>
						<th style={{ ...styles.th, width: '120px' }}>Reference</th>
						<th style={{ ...styles.th, width: '140px' }}>Scheduled Delivery</th>
						<th style={{ ...styles.th, width: '200px', minWidth: '200px' }}>ETA</th>
					</tr>
				</thead>
				<tbody>
					{segments.map((segment) => {
						const isSelected = selectedTripId === segment.trip_id
						const isSaved = savedTripIds.has(segment.trip_id)

						return (
							<tr
								key={segment.trip_id}
								style={{
									...styles.row,
									backgroundColor: isSelected ? '#eff6ff' : 'transparent',
								}}
								onClick={() => onSelectTrip(segment.trip_id)}
							>
								<td style={styles.td}>
									<button
										style={styles.saveButton}
										onClick={(e) => {
											e.stopPropagation()
											onToggleSave(segment.trip_id)
										}}
										title={isSaved ? 'Remove from saved' : 'Save shipment'}
									>
										{isSaved ? '★' : '☆'}
									</button>
								</td>
								<td style={{ ...styles.td, fontWeight: 600, color: '#1e40af' }}>
									{segment.pro_num}
								</td>
								<td style={styles.td}>{getStatusBadge(segment)}</td>
								<td style={{ ...styles.td, fontSize: '12px', fontWeight: 500 }}>
									{segment.customer_name || '—'}
								</td>
								<td style={{ ...styles.td, fontSize: '12px', color: '#6b7280' }}>
									{segment.meta?.bol_number || '—'}
								</td>
								<td style={{ ...styles.td, ...styles.locationCell }}>
									<div style={styles.locationName}>{segment.meta?.shipper || '—'}</div>
									<div style={styles.locationAddress}>
										{[segment.meta?.shipper_city, segment.meta?.shipper_state]
											.filter(Boolean)
											.join(', ') || ''}
									</div>
								</td>
								<td style={{ ...styles.td, ...styles.locationCell }}>
									<div style={styles.locationName}>{segment.meta?.consignee || '—'}</div>
									<div style={styles.locationAddress}>
										{[segment.meta?.consignee_city, segment.meta?.consignee_state]
											.filter(Boolean)
											.join(', ') || ''}
									</div>
								</td>
								<td style={{ ...styles.td, fontSize: '12px' }}>
									{formatTrailer(segment.meta?.trailer)}
								</td>
								<td style={{ ...styles.td, fontSize: '12px', color: '#6b7280' }}>
									{segment.meta?.reference || '—'}
								</td>
								<td style={{ ...styles.td, fontSize: '12px' }}>
									{formatDateTime(segment.consignee_schedule_time)}
								</td>
								<td style={{ ...styles.td, fontSize: '12px', whiteSpace: 'nowrap' }}>
									{formatDateTime(segment.meta?.delivery_eta)}
								</td>
							</tr>
						)
					})}
					{segments.length === 0 && (
						<tr>
							<td colSpan={11} style={styles.emptyState}>
								No shipments found
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	)
}

const styles: Record<string, React.CSSProperties> = {
	container: {
		width: '100%',
		height: '100%',
		borderTop: '1px solid #e5e7eb',
		overflowX: 'auto',
	},
	table: {
		width: '100%',
		minWidth: '1400px',
		borderCollapse: 'collapse',
		fontSize: '13px',
	},
	headerRow: {
		backgroundColor: '#f9fafb',
		borderBottom: '1px solid #e5e7eb',
	},
	th: {
		padding: '10px 12px',
		textAlign: 'left',
		fontWeight: 600,
		color: '#374151',
		fontSize: '12px',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		whiteSpace: 'nowrap',
	},
	row: {
		borderBottom: '1px solid #f3f4f6',
		cursor: 'pointer',
		transition: 'background-color 0.15s',
	},
	td: {
		padding: '12px',
		verticalAlign: 'middle',
	},
	saveButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: '16px',
		padding: '4px',
		borderRadius: '4px',
		transition: 'background-color 0.15s',
	},
	locationCell: {
		maxWidth: '200px',
	},
	locationName: {
		fontWeight: 500,
		color: '#111827',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	locationAddress: {
		fontSize: '11px',
		color: '#6b7280',
		marginTop: '2px',
	},
	emptyState: {
		textAlign: 'center',
		padding: '40px',
		color: '#6b7280',
		fontStyle: 'italic',
	},
}
